import api from './api';

// Check if push notifications are supported
export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// Get current notification permission
export function getPermissionStatus(): NotificationPermission {
  if (!('Notification' in window)) {
    return 'denied';
  }
  return Notification.permission;
}

// Request notification permission
export async function requestPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    throw new Error('Notifications not supported');
  }
  return Notification.requestPermission();
}

// Get VAPID public key from server
async function getVapidPublicKey(): Promise<string | null> {
  try {
    const response = await api.get('/push/vapid-public-key');
    return response.data.publicKey || null;
  } catch {
    console.error('[Push] Failed to get VAPID key');
    return null;
  }
}

// Convert base64 to Uint8Array for VAPID key
function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray.buffer;
}

// Subscribe to push notifications
export async function subscribeToPush(): Promise<boolean> {
  if (!isPushSupported()) {
    console.warn('[Push] Push notifications not supported');
    return false;
  }

  try {
    // Request permission first
    const permission = await requestPermission();
    if (permission !== 'granted') {
      console.log('[Push] Permission denied');
      return false;
    }

    // Get VAPID public key
    const vapidPublicKey = await getVapidPublicKey();
    if (!vapidPublicKey) {
      console.error('[Push] No VAPID key available');
      return false;
    }

    // Get service worker registration
    const registration = await navigator.serviceWorker.ready;

    // Subscribe to push
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });

    // Send subscription to server
    await api.post('/push/subscribe', {
      subscription: subscription.toJSON(),
    });

    console.log('[Push] Subscribed successfully');
    return true;
  } catch (error) {
    console.error('[Push] Subscription failed:', error);
    return false;
  }
}

// Unsubscribe from push notifications
export async function unsubscribeFromPush(): Promise<boolean> {
  if (!isPushSupported()) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      // Notify server
      await api.post('/push/unsubscribe', {
        endpoint: subscription.endpoint,
      });

      // Unsubscribe locally
      await subscription.unsubscribe();
      console.log('[Push] Unsubscribed successfully');
    }

    return true;
  } catch (error) {
    console.error('[Push] Unsubscribe failed:', error);
    return false;
  }
}

// Check if currently subscribed
export async function isSubscribed(): Promise<boolean> {
  if (!isPushSupported()) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch {
    return false;
  }
}

// Get push notification status from server
export async function getPushStatus(): Promise<{
  enabled: boolean;
  userEnabled: boolean;
  activeSubscriptions: number;
  hasActiveSubscription: boolean;
}> {
  try {
    const response = await api.get('/push/status');
    return response.data;
  } catch {
    return {
      enabled: false,
      userEnabled: true,
      activeSubscriptions: 0,
      hasActiveSubscription: false,
    };
  }
}

// Send a test notification
export async function sendTestNotification(): Promise<{
  success: boolean;
  message: string;
  devices: number;
}> {
  try {
    const response = await api.post('/push/test');
    return response.data;
  } catch (error) {
    console.error('[Push] Test notification failed:', error);
    return {
      success: false,
      message: 'Failed to send test notification',
      devices: 0,
    };
  }
}
